import {
	AbstractControl,
	AbstractControlOptions,
	AsyncValidatorFn,
	ControlValueAccessor,
	FormBuilder,
	FormControlName,
	FormGroup,
	NgControl,
	ValidationErrors,
	ValidatorFn,
} from '@angular/forms';
import {
	Directive,
	OnDestroy,
	OnInit,
	QueryList,
	ViewChildren,
} from '@angular/core';
import { collectErrors, touchControl } from '../utilities/utility-functions';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Purpose: makes sure that R is only composed of properties that exist in T
export type ExactSubset<T, R> = {
	[K in Extract<keyof T, keyof R>]: R[K];
};

export type FormConfig<T extends object = {}> = {
	[K in keyof Required<T>]:
		| [
				T[K] | '',
				(ValidatorFn | ValidatorFn[] | ValidationErrors)?,
				(AsyncValidatorFn | AsyncValidatorFn[] | ValidationErrors)?,
				AbstractControlOptions?
		  ]
		| AbstractControl;
};

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class CustomFormAccessor<T extends object>
	implements ControlValueAccessor, OnInit, OnDestroy {
	@ViewChildren(FormControlName) childControls: QueryList<FormControlName>;

	destroyed = new Subject<void>();
	form: FormGroup;

	errors: any;

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		public config: FormConfig<T>,
		public options: AbstractControlOptions = {}
	) {
		ngControl.valueAccessor = this;
		this.form = fb.group(config, options);
	}

	ngOnInit() {
		const control = this.ngControl.control;
		control.setValidators(this.validate);
		control.updateValueAndValidity();
	}

	// model --> view
	writeValue(value: T): void {
		if (value) {
			this.form.patchValue(value, { emitEvent: false });
			this.form.markAsUntouched();
		}
	}

	// view --> model
	registerOnChange(fn: (value: T) => void) {
		this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.form)(fn);
	}

	onTouched: () => void = () => {};

	validate = (): ValidationErrors => {
		const errors = collectErrors(this.form);
		this.errors = errors;
		return errors && errors.length > 0 ? errors : null;
	};

	setDisabledState?(isDisabled: boolean) {
		if (isDisabled) {
			this.form.disable({ emitEvent: false });
		} else {
			this.form.enable({ emitEvent: false });
		}
	}

	ngOnDestroy() {
		this.destroyed.next();
		this.destroyed.complete();
	}
}
